import React, { useEffect, useState } from 'react';
import { Box, Typography, Alert, TableRow, TableCell } from '@mui/material';
import { useNavigate, useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from 'store/store';
import { fetchDrawing } from 'store/actions/drawingActions';
import { usePermissions } from 'hooks/usePermissions';
import { CustomTextField } from 'components/FormFields/CustomTextField/CustomTextField';
import RedButton from 'Styled/RedButton';
import TextButton from 'Styled/TextButton';
import { PageHeader } from 'components/Layout/PageHeader';
import { StyledTable } from 'components/StyledTable/StyledTable';
import { useTableSort, type SortableColumn } from 'components/StyledTable/useTableSort';
import { apiGetPrizeCounts, apiUpdatePrizeCounts } from 'API/lotteryApi';
import type { IPrizeCount } from 'types/IPrizeCount';
import type { ApiError } from 'API/apiClient';
import { WorkflowStatusId } from 'types/enums/WorkflowStatus';
import { prizeLabel } from 'utility/prizeLabel';
import { isRsgvLottery } from 'utility/rsgvLottery';

const columns: SortableColumn<IPrizeCount>[] = [
  { name: 'Gewinn', width: '70%', sortBy: (row) => prizeLabel(row.typ, row.value, row.title) },
  { name: 'Anzahl', width: '30%', sortBy: (row) => row.times },
];

export const DrawingPrizeCountPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { lotteryId, drawingId } = useParams<{ lotteryId: string; drawingId: string }>();
  const { selectedDrawing } = useAppSelector((state) => state.drawings);
  const { isRedakteur } = usePermissions();
  const [prizeCounts, setPrizeCounts] = useState<IPrizeCount[]>([]);
  const { rows: sortedCounts, columns: tableColumns, sort } = useTableSort(columns, prizeCounts);
  const [times, setTimes] = useState<Record<number, string>>({});
  const [pageError, setPageError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const canRead = isRedakteur();
  const canWrite = isRedakteur();

  useEffect(() => {
    if (drawingId && canRead) {
      dispatch(fetchDrawing(Number(drawingId)));
    }
  }, [dispatch, drawingId, canRead]);

  useEffect(() => {
    if (!drawingId || !canRead) return;

    // The drawing can change while the answer is on its way.
    let current = true;
    apiGetPrizeCounts(Number(drawingId))
      .then((rows) => {
        if (!current) return;
        setPrizeCounts(rows);
        setTimes(Object.fromEntries(rows.map((row) => [row.prizeId, String(row.times)])));
      })
      .catch((error: ApiError) => {
        if (current) setPageError(error.message);
      });

    return () => {
      current = false;
    };
  }, [drawingId, canRead]);

  const backToList = () => navigate(`/lottery/${lotteryId}/drawings`);

  if (!canRead) {
    return (
      <Box>
        <PageHeader title="Gewinnanzahl" />
        <Alert severity="warning" sx={{ marginBottom: 2 }}>
          Sie haben keine Berechtigung, die Gewinnanzahl dieser Ziehung zu sehen.
        </Alert>
        <TextButton onClick={backToList}>Zurück</TextButton>
      </Box>
    );
  }

  const save = async () => {
    if (!drawingId || !canWrite || prizeCounts.length === 0) return;
    setPageError(null);
    setSaving(true);
    try {
      await apiUpdatePrizeCounts(
        Number(drawingId),
        prizeCounts.map((row) => ({ prizeId: row.prizeId, times: Number(times[row.prizeId] || 0) }))
      );
      backToList();
    } catch (error) {
      setPageError((error as ApiError).message);
    } finally {
      setSaving(false);
    }
  };

  const drawing = Number(selectedDrawing?.id) === Number(drawingId) ? selectedDrawing : null;

  const workflowStatus = drawing?.workflowDrawings?.[0]?.workflowStatusId;
  const isEditable =
    isRsgvLottery(lotteryId) || workflowStatus === WorkflowStatusId.OFFEN || workflowStatus === WorkflowStatusId.ZURUECKGEWIESEN;

  if (drawing && !isEditable) {
    return (
      <Box>
        <PageHeader title="Gewinnanzahl" />
        <Alert severity="warning" sx={{ marginBottom: 2 }}>
          Die Gewinnanzahl kann nur bearbeitet werden, solange die Ziehung offen oder zurückgewiesen ist.
        </Alert>
        <TextButton onClick={backToList}>Zurück</TextButton>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader title="Gewinnanzahl" />

      {pageError && <Alert severity="error">{pageError}</Alert>}

      {drawing && prizeCounts.length === 0 && !pageError && (
        <Typography variant="body1" sx={{ color: '#969696' }}>
          Dieser Ziehung sind keine Gewinne zugeordnet.
        </Typography>
      )}

      {drawing && prizeCounts.length > 0 && (
        <StyledTable columns={tableColumns} sort={sort}>
          {sortedCounts.map((row, index) => (
            <TableRow key={row.prizeId} className={`psf-styled-table__body-row ${index === sortedCounts.length - 1 ? 'psf-styled-table__body-last-row' : ''}`}>
              <TableCell>{prizeLabel(row.typ, row.value, row.title)}</TableCell>
              <TableCell>
                <CustomTextField
                  name={`prize-count-${row.prizeId}`}
                  value={times[row.prizeId] ?? ''}
                  onChange={(event) => setTimes({ ...times, [row.prizeId]: event.target.value.replace(/[^0-9]/g, '') })}
                />
              </TableCell>
            </TableRow>
          ))}
        </StyledTable>
      )}

      <Box sx={{ display: 'flex', gap: 2, marginTop: 2 }}>
        {!drawing ? (
          <TextButton onClick={backToList}>Zurück</TextButton>
        ) : (
          <>
            <RedButton onClick={() => void save()} disabled={saving || prizeCounts.length === 0}>
              Speichern
            </RedButton>
            <TextButton onClick={backToList}>Abbrechen</TextButton>
          </>
        )}
      </Box>
    </Box>
  );
};
